import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Clarification settings (per-user, persisted to localStorage).
 *
 * "Mode" is a frontend-only concept — the backend never learns about it.
 * Each mode maps onto the two primitives the backend understands,
 * ``allow_clarifications`` (can the agent ask?) and
 * ``clarification_timeout_secs`` (bounded wait, or indefinite?):
 *
 * | Mode                 | allow_clarifications | clarification_timeout_secs |
 * |----------------------|----------------------|----------------------------|
 * | ``auto``             | false                | n/a (omitted)              |
 * | ``ask_with_timeout`` | true                 | ``timeoutSecs`` (positive) |
 * | ``ask_and_wait``     | true                 | ``null`` (indefinite)      |
 *
 * ``timeoutSecs`` only applies to ``ask_with_timeout`` and is snapped to
 * ``ALLOWED_TIMEOUT_SECS``.
 */
// Single source of truth for the valid modes — the derived ``ClarificationMode``
// type and the migration's runtime guard both read from this.
export const CLARIFICATION_MODES = [
  'auto',
  'ask_with_timeout',
  'ask_and_wait',
] as const;
export type ClarificationMode = (typeof CLARIFICATION_MODES)[number];

/**
 * Session-scoped, per-workflow clarification override. Seeded from the
 * loaded workflow's ``metadata.default_clarification_mode`` (the "Clarifying
 * questions" setting in Edit Workflow Attributes) so the footer pill and
 * run dispatch reflect the workflow's default. Lives ONLY in memory — it is
 * deliberately excluded from the persisted localStorage state so a
 * workflow's default never overwrites the user's personal preference.
 */
export interface WorkflowClarificationSession {
  workflowId: string;
  mode: ClarificationMode;
  timeoutSecs: number;
  /**
   * The workflow-default values this session was seeded from. Used by
   * ``initWorkflowSession`` to tell a benign metadata refetch (same seed —
   * keep the user's session tweaks) apart from an edited workflow default
   * (seed changed — re-seed so the new default applies without a remount).
   */
  seededMode: ClarificationMode;
  seededTimeoutSecs: number;
}

interface ClarificationPrefsState {
  mode: ClarificationMode;
  timeoutSecs: number;
  workflowSession: WorkflowClarificationSession | null;
  setMode: (mode: ClarificationMode) => void;
  setTimeoutSecs: (secs: number) => void;
  initWorkflowSession: (
    workflowId: string,
    mode: ClarificationMode,
    timeoutSecs?: number | null,
  ) => void;
  clearWorkflowSession: () => void;
}

const DEFAULT_TIMEOUT_SECS = 60;

// Source of truth for the allowed values and their display labels — every
// mode/timeout picker (the chat settings popover, the workflow footer pill,
// the save-workflow modal's default) reads from these two lists rather than
// keeping its own copy.
export const CLARIFICATION_TIMEOUT_OPTIONS: {
  value: number;
  label: string;
}[] = [
  { value: 30, label: '30 seconds' },
  { value: 60, label: '1 minute' },
  { value: 120, label: '2 minutes' },
  { value: 300, label: '5 minutes' },
];

const ALLOWED_TIMEOUT_SECS = CLARIFICATION_TIMEOUT_OPTIONS.map(
  (option) => option.value,
);

export const CLARIFICATION_MODE_OPTIONS: {
  value: ClarificationMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'auto',
    label: 'Proceed without asking',
    description: 'Continue with an educated guess.',
  },
  {
    value: 'ask_with_timeout',
    label: 'Ask with timeout',
    description:
      'Ask, then continue with a best guess after the selected time.',
  },
  {
    value: 'ask_and_wait',
    label: 'Ask and wait',
    description: 'Pause the workflow until you answer.',
  },
];

export const snapTimeoutSecs = (secs: unknown): number => {
  const n = typeof secs === 'number' ? secs : Number(secs);
  if (!Number.isFinite(n)) return DEFAULT_TIMEOUT_SECS;
  // Snap to the nearest allowed value rather than rejecting, so a
  // stale freeform value lands on a real option (Radix Select renders
  // nothing when ``value`` matches no ``SelectItem``).
  let best: number = ALLOWED_TIMEOUT_SECS[0];
  let bestDelta = Math.abs(n - best);
  for (const candidate of ALLOWED_TIMEOUT_SECS) {
    const delta = Math.abs(n - candidate);
    if (delta < bestDelta) {
      best = candidate;
      bestDelta = delta;
    }
  }
  return best;
};

/**
 * Whether the chosen mode lets the agent ask at all — i.e. the legacy
 * ``allow_clarifications`` gate the backend still consumes. ``auto`` is
 * the only mode that suppresses asking.
 */
export const modeAllowsClarifications = (mode: ClarificationMode): boolean =>
  mode !== 'auto';

/**
 * The ``clarification_timeout_secs`` value to send for a given mode:
 *   - ``ask_with_timeout`` → the chosen ``timeoutSecs`` (bounded wait)
 *   - ``ask_and_wait``     → ``null`` (indefinite — backend parks with no
 *     timeout fallback)
 *   - ``auto``             → ``null`` (the agent never asks, so the value
 *     is irrelevant; callers omit it entirely when not allowed)
 */
export const clarificationTimeoutForMode = (
  mode: ClarificationMode,
  timeoutSecs: number,
): number | null => (mode === 'ask_with_timeout' ? timeoutSecs : null);

/** Runtime guard for metadata values coming off the wire. */
export const isClarificationMode = (
  value: unknown,
): value is ClarificationMode =>
  typeof value === 'string' &&
  (CLARIFICATION_MODES as readonly string[]).includes(value);

/**
 * The prefs the footer pill and run dispatch should use: the per-workflow
 * session override when one is active (workflow declared a default),
 * otherwise the user's persisted personal preference.
 *
 * ``isWorkflowScoped`` tells dispatch to send EXPLICIT values (including
 * ``allow_clarifications: false`` for auto) — with a workflow default in
 * play, omitting the keys would make the backend fall back to that
 * metadata default and ignore the user's session choice.
 */
export const effectiveClarificationPrefs = (
  state: Pick<
    ClarificationPrefsState,
    'mode' | 'timeoutSecs' | 'workflowSession'
  >,
): {
  mode: ClarificationMode;
  timeoutSecs: number;
  isWorkflowScoped: boolean;
} =>
  state.workflowSession
    ? {
        mode: state.workflowSession.mode,
        timeoutSecs: state.workflowSession.timeoutSecs,
        isWorkflowScoped: true,
      }
    : {
        mode: state.mode,
        timeoutSecs: state.timeoutSecs,
        isWorkflowScoped: false,
      };

export const useClarificationPrefsStore = create<ClarificationPrefsState>()(
  persist(
    (set) => ({
      mode: 'auto',
      timeoutSecs: DEFAULT_TIMEOUT_SECS,
      workflowSession: null,
      // While a workflow session override is active, mode/timeout edits are
      // session-scoped (per-workflow) and NEVER touch the persisted
      // personal preference — the workflow default seeded the session, and
      // the user is tweaking THIS workflow's run, not their global setting.
      setMode: (mode) =>
        set((state) =>
          state.workflowSession
            ? { workflowSession: { ...state.workflowSession, mode } }
            : { mode },
        ),
      setTimeoutSecs: (secs) =>
        set((state) =>
          state.workflowSession
            ? {
                workflowSession: {
                  ...state.workflowSession,
                  timeoutSecs: snapTimeoutSecs(secs),
                },
              }
            : { timeoutSecs: snapTimeoutSecs(secs) },
        ),
      initWorkflowSession: (workflowId, mode, timeoutSecs) =>
        set((state) => {
          const snappedTimeout =
            timeoutSecs != null
              ? snapTimeoutSecs(timeoutSecs)
              : DEFAULT_TIMEOUT_SECS;
          const existing = state.workflowSession;
          // Re-initialising the same workflow with an UNCHANGED default
          // (e.g. metadata refetch) must not clobber the user's session
          // tweaks. A changed seed means the workflow default was edited
          // (Edit Workflow Attributes) — re-seed so the new default takes
          // effect without a remount.
          if (
            existing?.workflowId === workflowId &&
            existing.seededMode === mode &&
            existing.seededTimeoutSecs === snappedTimeout
          ) {
            return {};
          }
          return {
            workflowSession: {
              workflowId,
              mode,
              timeoutSecs: snappedTimeout,
              seededMode: mode,
              seededTimeoutSecs: snappedTimeout,
            },
          };
        }),
      clearWorkflowSession: () =>
        set((state) =>
          state.workflowSession ? { workflowSession: null } : {},
        ),
    }),
    {
      name: 'eng-1613-clarification-prefs',
      version: 4,
      // Only the personal preference persists; the per-workflow session
      // override is memory-only by design (see WorkflowClarificationSession).
      partialize: (state) => ({
        mode: state.mode,
        timeoutSecs: state.timeoutSecs,
      }),
      // v1 -> v2 (May 2026): strip the now-unused ``maxPerRun`` key
      // after the BE dropped the per-run quota (cosmetic — persist
      // tolerates extra keys).
      // v2 -> v3 (May 2026): snap off-grid ``timeoutSecs`` to the
      // nearest allowed option after the freeform input became a
      // dropdown, so it renders correctly on first load.
      // v3 -> v4 (Jun 2026): replace the ``allow`` boolean with the
      // three-way ``mode`` enum. An old ``allow: true`` maps to
      // ``ask_with_timeout`` (the only ask-mode that existed); ``false``
      // / absent maps to ``auto``. ``ask_and_wait`` is opt-in only.
      migrate: (persistedState, _version) => {
        // The persisted shape is the partialized personal preference
        // (mode + timeoutSecs) — the session override never persists.
        type PersistedPrefs = Pick<
          ClarificationPrefsState,
          'mode' | 'timeoutSecs'
        >;
        if (!persistedState || typeof persistedState !== 'object') {
          return persistedState as PersistedPrefs;
        }
        const rest: Record<string, unknown> = {
          ...(persistedState as Record<string, unknown>),
        };
        if ('maxPerRun' in rest) {
          delete rest.maxPerRun;
        }
        if ('timeoutSecs' in rest) {
          rest.timeoutSecs = snapTimeoutSecs(rest.timeoutSecs);
        }
        if ('mode' in rest) {
          // Guard against corrupted / tampered localStorage: an unrecognized
          // ``mode`` would blank the trigger label and make
          // ``modeAllowsClarifications`` treat it as an ask-mode (any value
          // !== 'auto'), silently enabling clarifications. Fall back to the
          // safe default.
          if (
            !(CLARIFICATION_MODES as readonly string[]).includes(
              rest.mode as string,
            )
          ) {
            rest.mode = 'auto';
          }
        } else {
          rest.mode = rest.allow === true ? 'ask_with_timeout' : 'auto';
        }
        delete rest.allow;
        return rest as unknown as Pick<
          ClarificationPrefsState,
          'mode' | 'timeoutSecs'
        >;
      },
    },
  ),
);
